import { useState, useRef, useEffect, forwardRef } from "react";
import { ChevronDown, Check } from "lucide-react";
import { BaseComponentProps } from "@/shared/ui/base";

interface Option {
  value: string | number;
  label: string;
}

export interface SelectProps extends BaseComponentProps {
  value: string | number;
  onChange: (value: string | number) => void;
  options: Option[];
  placeholder?: string;
  icon?: React.ReactNode;
  label?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  compact?: boolean;
}

export const Select = forwardRef<HTMLDivElement, SelectProps>(
  (
    {
      value,
      onChange,
      options,
      placeholder = "Select...",
      icon,
      label,
      error,
      required,
      disabled,
      compact = false,
      className,
      "data-testid": dataTestId,
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Close on escape key
    useEffect(() => {
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          setIsOpen(false);
        }
      };

      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }, []);

    const selectedOption = options.find((opt) => opt.value === value);

    // Determine padding based on icon presence and compact mode
    const leftPadding = icon ? "pl-10" : compact ? "pl-3" : "pl-4";
    const verticalPadding = compact ? "py-2.5" : "py-3";

    const borderClass = error
      ? "border-red-500"
      : isOpen
        ? "border-red-500 shadow-[inset_0_0_0_1px_#ef4444]"
        : "border-gray-300 hover:border-gray-400";

    return (
      <div ref={ref} className={className} data-testid={dataTestId}>
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}

        <div ref={containerRef} className="relative">
          {/* Trigger Button */}
          <button
            type="button"
            onClick={() => !disabled && setIsOpen(!isOpen)}
            disabled={disabled}
            className={`w-full ${leftPadding} pr-10 ${verticalPadding} border rounded-lg bg-white text-left cursor-pointer transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed ${borderClass} ${compact ? "text-sm" : ""}`}
          >
            {/* Left Icon */}
            {icon && (
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                {icon}
              </div>
            )}

            {/* Selected Value */}
            <span
              className={selectedOption ? "text-gray-900" : "text-gray-400"}
            >
              {selectedOption ? selectedOption.label : placeholder}
            </span>

            {/* Chevron Icon */}
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <ChevronDown
                className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                  isOpen ? "rotate-180" : ""
                }`}
              />
            </div>
          </button>

          {/* Dropdown Menu */}
          {isOpen && !disabled && (
            <div className="absolute z-50 w-full mt-2 bg-white rounded-xl border border-gray-200 shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="max-h-64 overflow-y-auto py-2">
                {options.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      onChange(option.value);
                      setIsOpen(false);
                    }}
                    className={`w-full px-4 py-2.5 text-left flex items-center justify-between transition-colors ${
                      option.value === value
                        ? "bg-red-50 text-red-600"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <span className="font-medium">{option.label}</span>
                    {option.value === value && (
                      <Check className="w-4 h-4 text-red-600" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
      </div>
    );
  },
);

Select.displayName = "Select";
